import type { DomainError } from '@core/domain';
import { Spinner } from '@soteria/design-system-ve';

import { ErrorBanner } from '../ErrorBanner';

import * as styles from './Guide.css';
import { GuideCommunityPages } from './GuideCommunityPages';
import { GuideFavoritesSection } from './GuideFavoritesSection';
import { GuideGroupsList } from './GuideGroupsList';
import { GuideSystemPages } from './GuideSystemPages';
import type { CommunityGroupWithCommunities, GuideCommunity } from './useCommunityGroupsWithCommunities';

interface GuideContentProps {
  loading: boolean;
  errors: DomainError[] | null;
  activeTab: number;
  isSystemAdmin: boolean;
  communityGroups: CommunityGroupWithCommunities[];
  currentView: 'groups' | 'pages';
  selectedCommunity: GuideCommunity | null;
  communityGroupId: string | null;
  communityId?: string;
  selectedGroupId?: string;
  onCommunitySelect: (community: GuideCommunity) => void;
  onBack: () => void;
  onClose: () => void;
}

// Tab identifiers (Favorites is always enabled)
const TAB_FAVORITES = 0;
const TAB_COMMUNITIES = 1;
const TAB_SYSTEM = 2;

/**
 * GuideContent component
 * Handles conditional rendering of Guide tab content
 * Simplified to directly render community groups or pages without wrapper components
 */

export function GuideContent({
  loading,
  errors,
  activeTab,
  isSystemAdmin,
  communityGroups,
  currentView,
  selectedCommunity,
  communityGroupId,
  communityId,
  selectedGroupId,
  onCommunitySelect,
  onBack,
  onClose,
}: GuideContentProps) {
  if (loading) {
    return (
      <div className={styles.loadingState}>
        <Spinner />
      </div>
    );
  }

  if (errors) {
    return (
      <div className={styles.errorState}>
        <ErrorBanner errors={errors} />
      </div>
    );
  }

  // Favorites tab (always visible)
  if (activeTab === TAB_FAVORITES) {
    return <GuideFavoritesSection onClose={onClose} />;
  }

  // Communities tab
  if (activeTab === TAB_COMMUNITIES) {
    return currentView === 'groups' ? (
      <GuideGroupsList
        communityGroups={communityGroups}
        isSystemAdmin={isSystemAdmin}
        selectedCommunityId={communityId ?? null}
        selectedGroupId={selectedGroupId}
        onClose={onClose}
        onCommunitySelect={onCommunitySelect}
      />
    ) : selectedCommunity ? (
      <GuideCommunityPages community={selectedCommunity} communityGroupId={communityGroupId} onBack={onBack} onClose={onClose} />
    ) : null;
  }

  // System tab
  if (activeTab === TAB_SYSTEM && isSystemAdmin) {
    return <GuideSystemPages onClose={onClose} />;
  }

  return null;
}
